import { CommonModule } from "@angular/common";
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    EventEmitter,
    inject,
    input,
    Output,
} from "@angular/core";

import { Router } from "@angular/router";

import { LayoutFacade } from "@hermes/aphrodite-layout-state";
import { CTAAnchor, CTALink } from "@hermes/api-model-common";
import { LOCALE, WINDOW } from "@hermes/core-app-core";
import {
    RouterService,
    RouterServiceModule,
} from "@hermes/utils-generic-services-router";

import { NoFollowOnFilteredGridDirective } from "../../directives/no-follow-on-filtered-grid.directive";
import { isCtaAnchor, isCTALink } from "../../helpers/editorial-cta.helper";
import { transformSPALink } from "../../helpers/spa-navigation-for-cta.helper";
import { EditoAnalyticsService } from "../../services/edito-analytics.service";

const SCROLL_TO_PADDING = 120;

@Component({
    selector: "h-cta-container",
    imports: [
        CommonModule,
        RouterServiceModule,
        NoFollowOnFilteredGridDirective,
    ],
    templateUrl: "./cta-container.component.html",
    styleUrls: ["./cta-container.component.scss"],
    providers: [EditoAnalyticsService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CtaContainerComponent {
    readonly #locale = inject(LOCALE);
    readonly #editoAnalyticsService = inject(EditoAnalyticsService);
    readonly #routerService = inject(RouterService);
    readonly #layoutFacade = inject(LayoutFacade);
    readonly #router = inject(Router);
    readonly #window = inject<Window>(WINDOW);

    /**
     * Determines whether this component sends analytics events upon click
     * (i.e., when calling `mediaRedirection`).
     *
     * If set to `false`, analytics will only be sent from parent components,
     * and not from this component itself.
     *
     * Defaults to `true`.
     */
    public shouldSendDefaultRedirectionAnalytics = input(true, {
        transform: booleanAttribute,
    });

    public cta = input.required<CTALink | CTAAnchor>();

    public ariaLabel = input<string>();

    public focusOffset = input<number>();

    public tabIndexOverride = input<number>();

    @Output() public readonly mediaRedirectionEvent =
        new EventEmitter<CTALink>();

    public isKeyboardNavigation$ = this.#layoutFacade.keyboardNavigation$;

    public ctaAnchor = computed<CTAAnchor | undefined>(() => {
        const cta = this.cta();
        return isCtaAnchor(cta) ? cta : undefined;
    });

    public ctaLink = computed<CTALink | undefined>(() => {
        const cta = this.cta();
        return isCTALink(cta) ? cta : undefined;
    });

    public urlWithAnchor = computed(
        () => `${this.#router.url}#${this.ctaAnchor()?.id}`,
    );

    public mediaRedirection(event: MouseEvent, ctaLink: CTALink): void {
        event.preventDefault();
        event.stopPropagation();

        // Send analytics and emit event before triggering navigation
        if (this.shouldSendDefaultRedirectionAnalytics()) {
            this.#editoAnalyticsService.sendCTAClickEvent(ctaLink.path);
        }

        this.mediaRedirectionEvent.emit(ctaLink);

        const spaUrl = transformSPALink(this.#locale, ctaLink.path);
        this.#routerService.spaRedirect(spaUrl);
    }

    public scrollToElement(
        event: MouseEvent,
        ctaAnchor: CTAAnchor | undefined,
    ): void {
        event.preventDefault();
        event.stopPropagation();

        if (ctaAnchor) {
            const element = document.getElementById(ctaAnchor.id);
            if (!element) {
                return;
            }
            this.#window.scrollTo({
                top: element.offsetTop - SCROLL_TO_PADDING,
                behavior: "smooth",
            });
            element.focus({ preventScroll: true });
        }
    }
}
